import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SpecPanel from './SpecPanel';
import CostEstimatePanel from './CostEstimatePanel';
import SuggestionControl from './SuggestionControl';
import OutcomeSpecPanel from '../OutcomeSpecPanel';
import SearchableSelect from '../SearchableSelect';
import { unitsOfMeasureApi } from '../../utils/api';
import {
  fmtUSDc, fmtQty, rowComplete, completenessStatus,
  colsFor, fixedOf, measureCapFor, DELIVERY_STATUS, deliveryStamp, fmtStamp, varianceFor,
} from './helpers';

// PRD-031 Cluster E — worksheet grid v2 (mockups/economics-row-final.html,
// ratified 2026-07-30). One real <table> per card: a single column-header row
// frozen beneath the app's fixed chrome (--sticky-offset contract), one value
// line per Outcome, an annotation row beneath it (variance + timestamp), the
// rollup in <tfoot> INSIDE the table, and a Status column at the right edge.
// Labels, values and annotations are SEPARATE ROWS of one grid — never
// stacked children of a cell (the E4 alignment mechanic).
//
// The SAME component renders both lifecycle stages (house style,
// load-bearing): mode='proposal' (editable cells, no Actual track, no Status
// column pre-signing) and mode='agreement' (read-only, Estimated · Actual ·
// Margin · % · Status). Column sets per role come from colsFor (E2a) — cost
// tracks are ABSENT FROM THE DOM when the P&L is not visible, never hidden.

// --- compact in-cell Unit-of-Measure picker (registry, never free-text;
//     second iron pillar). Reuses the house SearchableSelect control. ---
function UomCell({ value, legacyName, onChange, disabled }) {
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    let cancelled = false;
    unitsOfMeasureApi.list({ context: 'sell' })
      .then((r) => { if (!cancelled) setCatalog(r.data.unitsOfMeasure || []); })
      .catch(() => { if (!cancelled) setCatalog([]); });
    return () => { cancelled = true; };
  }, []);
  const resolved = value != null && catalog.some((u) => u.id === value);
  return (
    <div>
      <SearchableSelect
        options={catalog}
        value={value}
        onChange={(id, name) => onChange && onChange(id || null, name || '')}
        placeholder="Select a unit…"
        disabled={disabled}
      />
      {!resolved && legacyName && (
        <p className="text-[10px] text-estimated mt-0.5 truncate" title={legacyName}>“{legacyName}” — pick a unit</p>
      )}
    </div>
  );
}

// PRD-027 WU-G — the Outcome's active (delivery/consumption) window,
// editable in Proposal+Agency only (native date inputs).
function ActiveWindowChip({ from, to, editable, onChange }) {
  if (!editable) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-2 py-1">
      <input type="date" value={from || ''} onChange={(e) => onChange({ activeFrom: e.target.value || null })}
        className="bg-transparent text-primary-700 text-[11px] tabular-nums outline-none w-[112px]" />
      <span className="text-gray-600">–</span>
      <input type="date" value={to || ''} onChange={(e) => onChange({ activeTo: e.target.value || null })}
        className="bg-transparent text-primary-700 text-[11px] tabular-nums outline-none w-[112px]" />
    </span>
  );
}

// PRD-024 / PRD-026 F3 — Agreement read-only inline cost lines (Estimated,
// frozen · Actual, live per Cost Type). Cost-side only (Principle #6).
//
// WU-B defect 1 (staging fix-it sprint 2026-08): these are ROWS OF THE
// SAME TABLE, so each amount is a <td> in the card's Estimated/Actual
// column tracks — aligned with the header row and the economics row by
// construction, at every width and in every font. The previous nested
// div-grid (fixed 120px tracks in a colSpan cell) only coincidentally
// lined up and drifted into a detached right-side block on staging. Per
// the ratified mockup the tier carries NO mini column headers — the
// card's one frozen header row labels the columns (PRD-026 F5).
// cols: 0..4 identity/terms · 5 est · 6 act · 7 marg · 8 pct · 9 status.
function AgreementCostLineRows({ lines = [], estimatedSubtotalCents = 0, actualSubtotalCents = 0, cols }) {
  const N = cols.length;
  const tail = N - 7; // marg + pct + status stay empty in this tier
  return (
    <>
      <tr className="cl-head">
        <td colSpan={5} className="cl-title">Cost — this outcome</td>
        <td colSpan={N - 5} />
      </tr>

      {lines.length === 0 && (
        <tr className="cl-line">
          <td colSpan={N} className="cl-empty">No consumption logged yet.</td>
        </tr>
      )}

      {lines.map((line, i) => (
        <tr key={i} className="cl-line">
          <td colSpan={5} className="cl-name" title={line.name}>
            <span className="cl-dot" />
            <span className="cl-nm">{line.name}</span>
          </td>
          <td data-col="5" className="money cl-est">
            {/* PRD-026 F2b — an absent estimate renders "—", NEVER the actual. */}
            {line.estimated_cost_cents != null ? fmtUSDc(line.estimated_cost_cents) : '—'}
            {line.estimated_cost_cents != null && (
              <small>{line.estimated_quantity > 1 ? `${fmtQty(line.estimated_quantity)} × ${fmtUSDc(line.rate_snapshot_cents)}` : 'flat'}</small>
            )}
          </td>
          <td data-col="6" className="money cl-act">
            {line.cost_cents != null ? fmtUSDc(line.cost_cents) : '—'}
            {line.cost_cents != null && (
              <small>{line.quantity > 1 ? `${fmtQty(line.quantity)} × ${fmtUSDc(line.rate_cents)}` : 'flat'}</small>
            )}
          </td>
          {tail > 0 && <td colSpan={tail} />}
        </tr>
      ))}

      <tr className="cl-sub">
        <td colSpan={5} className="cl-sub-label">Outcome cost</td>
        <td data-col="5" className="money cl-est cl-sub-val">{fmtUSDc(estimatedSubtotalCents)}</td>
        <td data-col="6" className="money cl-act cl-sub-val">{fmtUSDc(actualSubtotalCents)}</td>
        {tail > 0 && <td colSpan={tail} />}
      </tr>
    </>
  );
}

const CHEV = (
  <span className="chev" aria-hidden="true">
    <svg width="7" height="11" viewBox="0 0 7 11" fill="none">
      <path d="M1.4 1.2 5.6 5.5 1.4 9.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

// Pick the suggestion to surface for a target: the open one, else the latest
// resolved (so history stays visible).
function pickSuggestion(suggestions, matchFn) {
  const matches = (suggestions || []).filter(matchFn);
  return matches.find((s) => s.status === 'open') || matches[matches.length - 1] || null;
}

function Colgroup({ cols }) {
  const F = fixedOf(cols);
  return (
    <colgroup>
      {cols.map((c) => (
        <col key={c.k} className={`c-${c.k}`}
          style={{ width: c.w ? `${c.w}px` : `calc((100% - ${F}px) * ${c.f})` }} />
      ))}
    </colgroup>
  );
}

function HeadRow({ cols }) {
  return (
    <thead>
      <tr>
        {cols.map((c, i) => {
          const extra = c.a !== c.h ? { 'aria-label': c.a, title: c.a } : {};
          return (
            <th key={c.k} scope="col" className="ch" data-col={i} {...extra}>
              <span className="n">{c.h}</span>
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

// E5b/F5 — the timestamp cell spans THE LAST THREE COLUMNS in every
// configuration so the Client's acceptance-pending string fits; its right box
// edge is still the Status column's right edge, so the axis is unchanged.
// The variance cell's right edge is Actual's edge (colspan construction).
function AnnoRow({ cols, showCost, variance, stamp }) {
  const N = cols.length;
  const ts = stamp || '';
  return (
    <tr className="anno rowlink">
      {showCost ? (
        <>
          <td colSpan={N - 5} />
          <td colSpan={2}>{variance && <span className={`var ${variance.cls}`}>{variance.text}</span>}</td>
          <td colSpan={3} title={ts}><span className="ts">{ts}</span></td>
        </>
      ) : (
        <>
          <td colSpan={N - 3} />
          <td colSpan={3} title={ts}><span className="ts">{ts}</span></td>
        </>
      )}
    </tr>
  );
}

// ---- Agreement-mode Outcome: economics rows exactly per the ratified card,
//      then the retained pre-Cluster-D surfaces (inline cost lines,
//      verification actions, locked spec) as full-width detail rows. ----
function AgreementOutcomeGroup({ row, cols, showCost, isBuyer, agreementId, renderRowActions }) {
  const navigate = useNavigate();
  const N = cols.length;
  const st = DELIVERY_STATUS[row.delivery?.status] || null;
  const stamp = deliveryStamp(row, { isBuyer });
  const revCents = row.revenueCents || 0;
  const estCost = row.estimatedCostCents != null ? row.estimatedCostCents : 0;
  const actCost = row.costCents != null ? row.costCents : 0;
  const liveMargin = row.marginCents != null ? row.marginCents : revCents - actCost;
  const liveMarginPct = row.marginPct != null ? row.marginPct : (revCents > 0 ? (liveMargin / revCents) * 100 : 0);
  // E5/F5 — the row's one action: the Delivery sub-page, anchored to this
  // Outcome (PRD-031 F6).
  const href = `/agreements/${agreementId}/delivery?outcome=${row.outcomeId}`;
  const goToDetails = () => { navigate(href); };

  return (
    <>
      <tbody className="oc" onClick={goToDetails}>
        <tr className="rowlink">
          <th className="orow" colSpan={N} scope="rowgroup">
            <a className="oname" href={href} aria-label={`Delivery details — ${row.name}`}
              onClick={(e) => { e.preventDefault(); goToDetails(); }}>{row.name}</a>
          </th>
        </tr>
        <tr className="vals rowlink">
          <td data-col="0" title={row.sellUnitName || ''}><span className="txt">{row.sellUnitName || '—'}</span></td>
          <td data-col="1" title={row.sellUnitNoun || ''}><span className="txt">{row.sellUnitNoun || '—'}</span></td>
          <td data-col="2" className="money term">{row.sellPricePerUnitCents != null ? fmtUSDc(row.sellPricePerUnitCents) : '—'}</td>
          <td data-col="3" className="money term">{row.sellQuantity != null ? fmtQty(row.sellQuantity) : '—'}</td>
          <td data-col="4" className="money rev">{fmtUSDc(revCents)}</td>
          {showCost && (
            <>
              <td data-col="5" className="money estv">{fmtUSDc(estCost)}</td>
              <td data-col="6" className="money actv">{fmtUSDc(actCost)}</td>
              <td data-col="7" className="money marg">{fmtUSDc(liveMargin)}</td>
              <td data-col="8" className="money pct">{Math.round(liveMarginPct)}%</td>
            </>
          )}
          <td data-col={N - 1} className="stat" title={st ? st.word : ''}>
            {st
              ? <span className={`sword ${st.cls}`}>{st.word}</span>
              : <span className="sword" style={{ visibility: 'hidden' }}>{' '}</span>}
            {CHEV}
          </td>
        </tr>
        <AnnoRow cols={cols} showCost={showCost}
          variance={showCost ? varianceFor(actCost, estCost) : null} stamp={stamp} />
      </tbody>

      {(showCost || renderRowActions || (row.deliverables || []).length > 0) && (
        <tbody className="oc-detail">
          {showCost && (
            <AgreementCostLineRows lines={row.costLines || []} estimatedSubtotalCents={estCost} actualSubtotalCents={actCost} cols={cols} />
          )}
          {renderRowActions && (
            <tr className="detail"><td colSpan={N}><div className="px-4 pt-2 pb-2">{renderRowActions(row)}</div></td></tr>
          )}
          {(row.deliverables || []).length > 0 && (
            <tr className="detail"><td colSpan={N}>
              <div className="px-4 pt-1 pb-3">
                <div className="text-[10px] text-gray-600 uppercase tracking-wide mb-1">Agreed spec · v1</div>
                {row.deliverables.map((d) => (
                  <OutcomeSpecPanel key={d.id} agreementId={agreementId} deliverableId={d.id} deliverableName={d.name || row.name} />
                ))}
              </div>
            </td></tr>
          )}
        </tbody>
      )}
    </>
  );
}

// ---- Proposal-mode Outcome: the same table, cells as INPUTS. ----
function ProposalOutcomeGroup({
  row, cols, showCost, isBuyer, editable, proposalId, actorSide,
  onSellFieldChange, onRemoveOutcome, onAddCost, onRemoveCost,
  suggestions, suggestionHandlers, mySide, canSuggest,
}) {
  const N = cols.length;
  const priceSug = pickSuggestion(suggestions, (s) => s.target_type === 'sell_price_per_unit' && s.target_outcome_id === row.outcomeId);
  const qtySug = pickSuggestion(suggestions, (s) => s.target_type === 'sell_quantity' && s.target_outcome_id === row.outcomeId);
  // Local edit buffers so typing is smooth; commit on blur.
  const [unitName, setUnitName] = useState(row.sellUnitName || '');
  const [price, setPrice] = useState(row.sellPricePerUnitCents != null ? (row.sellPricePerUnitCents / 100).toString() : '');
  const [qty, setQty] = useState(row.sellQuantity != null ? String(row.sellQuantity) : '');

  useEffect(() => { setUnitName(row.sellUnitName || ''); }, [row.sellUnitName]);
  useEffect(() => { setPrice(row.sellPricePerUnitCents != null ? (row.sellPricePerUnitCents / 100).toString() : ''); }, [row.sellPricePerUnitCents]);
  useEffect(() => { setQty(row.sellQuantity != null ? String(row.sellQuantity) : ''); }, [row.sellQuantity]);

  const complete = rowComplete(row);
  const commitName = () => { if (unitName !== (row.sellUnitName || '')) onSellFieldChange(row.outcomeId, { sellUnitName: unitName }); };
  const commitPrice = () => {
    const v = price === '' ? null : parseFloat(price);
    const cur = row.sellPricePerUnitCents != null ? row.sellPricePerUnitCents / 100 : null;
    if (v !== cur) onSellFieldChange(row.outcomeId, { sellPricePerUnit: v });
  };
  const commitQty = () => {
    const v = qty === '' ? null : parseFloat(qty);
    if (v !== (row.sellQuantity != null ? row.sellQuantity : null)) onSellFieldChange(row.outcomeId, { sellQuantity: v });
  };

  // Live revenue from the local buffers (updates as you type).
  const liveRevCents = Math.round((parseFloat(price) || 0) * (parseFloat(qty) || 0) * 100);
  const estCost = row.estimatedCostCents || 0;

  return (
    <>
      <tbody className="oc" id={`outcome-row-${row.outcomeId}`}>
        <tr>
          <th className="orow group" colSpan={N} scope="rowgroup">
            <span className="oname">{row.name}</span>
            <span className="inline-flex items-center gap-2 ml-3 align-middle">
              <ActiveWindowChip from={row.activeFrom} to={row.activeTo} editable={editable && !isBuyer}
                onChange={(patch) => onSellFieldChange(row.outcomeId, patch)} />
              {!complete && (
                <span className="text-[10px] font-bold text-amber-800 bg-amber-200 rounded px-1.5 py-0.5">needs price + unit</span>
              )}
              {/* PRD-027 WU-C suggestions anchor to the Outcome row (the fixed
                  money tracks have no room for an inline affordance). */}
              {(priceSug || canSuggest) && (
                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold text-gray-600 uppercase tracking-wide ${priceSug ? '' : 'opacity-0 group-hover:opacity-100 transition-opacity'}`}>
                  Price
                  <SuggestionControl target={{ type: 'sell_price_per_unit', outcomeId: row.outcomeId }} suggestion={priceSug}
                    side={mySide} canSuggest={canSuggest} currentValue={row.sellPricePerUnitCents != null ? row.sellPricePerUnitCents / 100 : null} handlers={suggestionHandlers} />
                </span>
              )}
              {(qtySug || canSuggest) && (
                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold text-gray-600 uppercase tracking-wide ${qtySug ? '' : 'opacity-0 group-hover:opacity-100 transition-opacity'}`}>
                  Qty
                  <SuggestionControl target={{ type: 'sell_quantity', outcomeId: row.outcomeId }} suggestion={qtySug}
                    side={mySide} canSuggest={canSuggest} currentValue={row.sellQuantity} handlers={suggestionHandlers} />
                </span>
              )}
              {editable && (
                <button className="text-[11px] text-gray-600 hover:text-coral-700" onClick={() => onRemoveOutcome(row.outcomeId)}>Remove</button>
              )}
            </span>
          </th>
        </tr>
        <tr className="vals">
          <td data-col="0">
            {editable ? (
              <input className="cellinput" value={unitName} placeholder="e.g., Verified install"
                onChange={(e) => setUnitName(e.target.value)} onBlur={commitName} />
            ) : (
              <span className="txt" title={row.sellUnitName || ''}>{row.sellUnitName || '—'}</span>
            )}
          </td>
          <td data-col="1" style={editable ? { overflow: 'visible' } : undefined}>
            {editable ? (
              <UomCell value={row.sellUnitOfMeasureId} legacyName={row.sellUnitNoun}
                onChange={(id, name) => onSellFieldChange(row.outcomeId, { sellUnitOfMeasureId: id, sellUnitOfMeasure: name })} />
            ) : (
              <span className="txt" title={row.sellUnitNoun || ''}>{row.sellUnitNoun || '—'}</span>
            )}
          </td>
          <td data-col="2">
            {editable ? (
              <input className="cellinput num" inputMode="decimal" value={price} placeholder="0.00"
                onChange={(e) => setPrice(e.target.value)} onBlur={commitPrice}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
            ) : (
              <span className="money term">{row.sellPricePerUnitCents != null ? fmtUSDc(row.sellPricePerUnitCents) : '—'}</span>
            )}
          </td>
          <td data-col="3">
            {editable ? (
              <input className="cellinput num" inputMode="decimal" value={qty} placeholder="0"
                onChange={(e) => setQty(e.target.value)} onBlur={commitQty}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
            ) : (
              <span className="money term">{row.sellQuantity != null ? fmtQty(row.sellQuantity) : '—'}</span>
            )}
          </td>
          <td data-col="4" className="money rev">{fmtUSDc(editable ? liveRevCents : (row.revenueCents || 0))}</td>
          {showCost && <td data-col="5" className="money estv">{fmtUSDc(estCost)}</td>}
        </tr>
      </tbody>

      <tbody className="oc-detail">
        {showCost && (
          <tr className="detail"><td colSpan={N}>
            <CostEstimatePanel
              proposalId={proposalId} outcomeId={row.outcomeId}
              costLines={row.costLines || []} estimatedCostCents={estCost}
              editable={editable} onAdd={onAddCost} onRemove={onRemoveCost}
            />
          </td></tr>
        )}
        <tr className="detail"><td colSpan={N}>
          <div className="px-4 pt-3 pb-2">
            {row.deliverableId ? (
              <SpecPanel proposalId={proposalId} deliverableId={row.deliverableId} editable={editable} actorSide={actorSide}
                suggestions={suggestions} suggestionHandlers={suggestionHandlers} mySide={mySide} canSuggest={canSuggest} />
            ) : (
              <p className="text-xs text-gray-600">Spec appears once the Outcome has a deliverable.</p>
            )}
          </div>
        </td></tr>
      </tbody>
    </>
  );
}

export default function Worksheet({
  mode = 'proposal',
  proposalId, agreementId, rows, showCost, editable, actorSide = 'seller',
  totals, disclosure, isBuyer = false, caption,
  onSellFieldChange, onAddOutcome, onRemoveOutcome, onAddCost, onRemoveCost,
  // agreement-mode extras
  isSeller, onEvidenceClick, renderRowActions,
  // PRD-027 WU-C — buyer-suggestion layer (proposal stage only).
  suggestions = [], suggestionHandlers, mySide = 'seller', canSuggest = false,
}) {
  const isAgreement = mode === 'agreement';
  const cols = colsFor({ mode, showCost, isBuyer, rows, totals });
  const N = cols.length;
  const cap = measureCapFor(cols);
  const status = completenessStatus(rows);
  // The Agreement total row keeps its name in every configuration (ratified
  // mockup); the Proposal footer keeps the role relabel.
  const totalLabel = isAgreement ? 'Agreement total' : (isBuyer ? 'Total contract price' : 'Proposal total');
  // The Margin column foots itself: rollup = revenue − actual cost, the same
  // number as the Realized-margin hero (economics-row-final.html). The
  // expected-margin basis (revenue − GREATEST(est, actual)) no longer
  // renders on the grid — PRD-031 Cluster E supersedes the 2026-07-05
  // footer-basis display.
  const totMarginCents = (totals.revenueCents || 0) - (totals.costCents || 0);
  const totMarginPct = totals.revenueCents > 0 ? (totMarginCents / totals.revenueCents) * 100 : 0;

  // Rollup annotation: variance on the totals + the freshest check moment.
  const lastChecked = isAgreement
    ? rows.reduce((max, r) => {
        const t = r.delivery?.last_checked_at;
        return t && (!max || new Date(t) > new Date(max)) ? t : max;
      }, null)
    : null;
  const asOf = lastChecked ? `as of ${fmtStamp(lastChecked)}` : '';

  return (
    <div className="ws-sheet mx-auto w-full" style={{ maxWidth: `${cap}px` }}>
      <table className={`ws ${isAgreement ? 'ws-agmt' : 'ws-prop'}`}>
        <caption>
          <span className="cap">
            <span className="cap-t">{caption?.title || (isAgreement ? 'Signed Agreement · per-outcome economics' : 'Proposal · per-outcome economics')}</span>
            {caption?.right && <span className="cap-r">{caption.right}</span>}
          </span>
        </caption>
        <Colgroup cols={cols} />
        <HeadRow cols={cols} />

        {rows.length === 0 && (
          <tbody>
            <tr><td colSpan={N} className="px-4 py-8 text-center text-sm text-gray-600" style={{ textAlign: 'center', whiteSpace: 'normal' }}>
              {isAgreement ? 'No outcomes on this Agreement.' : `No outcomes yet.${editable ? ' Add your first Outcome below.' : ''}`}
            </td></tr>
          </tbody>
        )}

        {isAgreement
          ? rows.map((row) => (
            <AgreementOutcomeGroup key={row.outcomeId} row={row} cols={cols} showCost={showCost}
              isBuyer={isBuyer} agreementId={agreementId} renderRowActions={renderRowActions} />
          ))
          : rows.map((row) => (
            <ProposalOutcomeGroup key={row.outcomeId} row={row} cols={cols} showCost={showCost}
              isBuyer={isBuyer} editable={editable} proposalId={proposalId} actorSide={actorSide}
              onSellFieldChange={onSellFieldChange} onRemoveOutcome={onRemoveOutcome}
              onAddCost={onAddCost} onRemoveCost={onRemoveCost}
              suggestions={suggestions} suggestionHandlers={suggestionHandlers} mySide={mySide} canSuggest={canSuggest} />
          ))}

        {rows.length > 0 && (
          <tfoot>
            <tr className="tot">
              <th className="flbl" colSpan={4} scope="row">
                {totalLabel} <small>· {rows.length} outcome{rows.length === 1 ? '' : 's'}</small>
                {!isAgreement && !status.empty && !status.complete && (
                  <span className="ml-2 text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 normal-case tracking-normal">
                    {status.incompleteCount} incomplete
                  </span>
                )}
              </th>
              <td data-col="4" className="money rev">{fmtUSDc(totals.revenueCents)}</td>
              {showCost && (
                <>
                  <td data-col="5" className="money estv">{fmtUSDc(totals.estimatedCostCents)}</td>
                  {isAgreement && (
                    <>
                      <td data-col="6" className="money actv">{fmtUSDc(totals.costCents)}</td>
                      <td data-col="7" className="money marg">{fmtUSDc(totMarginCents)}</td>
                      <td data-col="8" className="money pct">{Math.round(totMarginPct)}%</td>
                    </>
                  )}
                </>
              )}
              {isAgreement && <td data-col={N - 1} className="stat" />}
            </tr>
            {isAgreement && (
              <AnnoRow cols={cols} showCost={showCost}
                variance={showCost ? varianceFor(totals.costCents, totals.estimatedCostCents) : null}
                stamp={asOf} />
            )}
          </tfoot>
        )}
      </table>

      {/* Add Outcome — proposal/editable only (Agreements are immutable) */}
      {!isAgreement && editable && (
        <div className="px-4 py-3 border-t border-gray-200 bg-white">
          <button className="text-sm font-semibold text-primary-600 hover:text-primary-700" onClick={onAddOutcome}>+ Add Outcome</button>
        </div>
      )}

      {/* The Verdict fee is the Agency's cost of using the platform —
          suppressed in BOTH Client configurations, including the shared one
          (sharing the P&L shares the Agreement's economics, not Verdict's
          invoice to the Agency). When the 5% is assessed is a commercial
          term, not narration (E9). */}
      {isAgreement && isSeller && disclosure && (
        <div className="ws-feenote">
          Verdict fee: <b>{Math.round(disclosure.takeRatePct || 5)}% of Agreement value ({fmtUSDc(disclosure.takeRateCents)})</b>, assessed when every outcome is delivered and accepted.
        </div>
      )}
    </div>
  );
}
